import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import SelectInput from "../shared/Inputs/SelectInput";
import LoadingWheel from "../shared/LoadingWheel";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useMaterials from "../../data/useMaterials";
import usePartCategoryMaterials from "../../data/usePartCategoryMaterials";
import { IMaterial } from "../../models/Material";
import { IPartCategoryMaterial } from "../../models/PartCategoryMaterial";

type CategoryMaterialRowFormValues = {
  materialId: string;
  materialPercentage: number;
};

function CategoryMaterialRow({
  categoryId,
  link,
  materials,
  usedMaterialIds,
}: {
  categoryId: string;
  link: IPartCategoryMaterial | null;
  materials: IMaterial[];
  usedMaterialIds: string[];
}) {
  const isNew = link === null;
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Category Material");
  const { create, update, remove } = usePartCategoryMaterials(categoryId);
  const { register, handleSubmit, reset, setValue, watch } = useForm<CategoryMaterialRowFormValues>({
    defaultValues: {
      materialId: link?.materialId ?? "",
      materialPercentage: link?.materialPercentage ?? 0,
    },
  });

  useEffect(() => {
    reset({
      materialId: link?.materialId ?? "",
      materialPercentage: link?.materialPercentage ?? 0,
    });
  }, [link, reset]);

  const materialOptions = materials
    .filter((material) => {
      if (!usedMaterialIds.includes(material.id)) {
        return true;
      }

      return link?.materialId === material.id;
    })
    .map((material) => ({
      id: material.id,
      name: material.name,
    }));

  const onSubmit = (data: CategoryMaterialRowFormValues) => {
    const payload = {
      materialId: data.materialId,
      materialPercentage: Number(data.materialPercentage),
    };

    if (!payload.materialId) {
      saveHandlers.onError();
      return;
    }

    if (isNew) {
      create.mutate(payload, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          reset({
            materialId: "",
            materialPercentage: 0,
          });
        },
        onError: saveHandlers.onError,
      });
      return;
    }

    update.mutate(
      {
        linkId: link!.id,
        req: payload,
      },
      saveHandlers
    );
  };

  const handleDelete = () => {
    if (!link) {
      return;
    }

    remove.mutate(link.id, deleteHandlers);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="border border-gray-200 rounded-md p-3 bg-white">
      <div className="grid grid-cols-12 gap-x-4 gap-y-3 items-end">
        <div className="col-span-6">
          <SelectInput
            label="Material"
            register={register}
            registerName="materialId"
            options={materialOptions}
            setValue={setValue}
            watch={watch}
          />
        </div>
        <div className="col-span-3">
          <NumberInput
            label="Percentage"
            registerName="materialPercentage"
            register={register}
            min={0}
            step=".01"
          />
        </div>
        <div className="col-span-3 flex items-end gap-2 pb-0.5">
          <ButtonInput
            label={isNew ? "Create" : "Save"}
            isSubmit={true}
            onClick={undefined}
            classes={undefined}
            isLoading={create.isLoading || update.isLoading}
          />
          {!isNew && (
            <ButtonInput
              label="Delete"
              isSubmit={false}
              onClick={handleDelete}
              classes={"!bg-red-500 hover:!bg-red-600"}
              isLoading={remove.isLoading}
            />
          )}
        </div>
      </div>
    </form>
  );
}

export default function CategoryMaterials({ categoryId }: { categoryId: string }) {
  const { materials } = useMaterials();
  const { partCategoryMaterials } = usePartCategoryMaterials(categoryId);

  const isLoading = materials.isLoading || partCategoryMaterials.isLoading;
  const allMaterials = materials.data?.data ?? [];
  const links = partCategoryMaterials.data?.data ?? [];
  const usedMaterialIds = links.map((link) => link.materialId);
  const totalPercentage = links.reduce((total, link) => total + link.materialPercentage, 0);
  const isOverTotal = totalPercentage > 100;

  if (isLoading) {
    return (
      <div className="w-full py-4">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-lg">Category Materials</h1>
        <span className={`${isOverTotal ? "text-red-600" : "text-gray-600"} text-sm font-medium`}>
          Total: {totalPercentage.toFixed(2)}%
        </span>
      </div>

      {isOverTotal && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Material total is above 100%. Saving is still allowed.
        </div>
      )}

      {allMaterials.length === 0 ? (
        <div className="text-sm text-gray-600 border border-gray-200 rounded-md bg-white p-3">
          No materials found. Create materials in Admin before assigning them to a category.
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <CategoryMaterialRow
              key={link.id}
              categoryId={categoryId}
              link={link}
              materials={allMaterials}
              usedMaterialIds={usedMaterialIds}
            />
          ))}
          <CategoryMaterialRow
            categoryId={categoryId}
            link={null}
            materials={allMaterials}
            usedMaterialIds={usedMaterialIds}
          />
        </div>
      )}
    </div>
  );
}
